import axios from "axios";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
} from "@getgreenline/blaze-ui";
import { Mail, TriangleAlert, UserPlus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import {
  useAssignBrandUser,
  useStaffBrandUserByEmail,
} from "@/hooks/use-staff-brand-users";

type BrandUserAssignmentFormProps = {
  brand: { id: string; name: string };
  onCancel: () => void;
  onSuccess: () => void;
  cancelLabel?: string;
};

function getErrorDetail(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string" && detail.length > 0) return detail;
  }
  return fallback;
}

export function BrandUserAssignmentForm({
  brand,
  onCancel,
  onSuccess,
  cancelLabel = "Cancel",
}: BrandUserAssignmentFormProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const lookup = useStaffBrandUserByEmail(email);
  const assignment = useAssignBrandUser();

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setIsInviting(false);
    assignment.reset();
  };

  const lookupMatchesInput = lookup.isValidEmail && !lookup.isDebouncing;
  const user = lookupMatchesInput && lookup.isSuccess ? lookup.data : null;
  const userIsMissing =
    lookupMatchesInput && lookup.isSuccess && lookup.data === null;
  const namesAreValid =
    firstName.trim().length > 0 && lastName.trim().length > 0;
  const canSubmit =
    !assignment.isPending &&
    ((user !== null && lookup.isValidEmail) ||
      (isInviting && lookup.isValidEmail && namesAreValid));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    try {
      const result = await assignment.mutateAsync({
        brandId: brand.id,
        email: lookup.normalizedEmail,
        ...(isInviting
          ? {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
            }
          : {}),
      });

      toast.success(
        result.status === "invited" ? "Invitation sent" : "User assigned",
        {
          description:
            result.status === "invited"
              ? `${result.email} was invited and assigned to ${brand.name}.`
              : `${result.email} was assigned to ${brand.name}.`,
        },
      );
      onSuccess();
    } catch {
      // The mutation state renders the actionable error while preserving input.
    }
  };

  return (
    <form onSubmit={submit} className="tw:space-y-5">
      <DialogHeader>
        <DialogTitle>Assign user to {brand.name}</DialogTitle>
        <DialogDescription>
          Look up an existing brands account or invite a new user.
        </DialogDescription>
      </DialogHeader>

      <div className="tw:space-y-2">
        <Label htmlFor="brand-user-email">Email address</Label>
        <Input
          id="brand-user-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => handleEmailChange(event.target.value)}
          placeholder="person@example.com"
        />
        {email.length > 0 && !lookup.isValidEmail ? (
          <p className="tw:text-sm tw:text-destructive">
            Enter a complete email address.
          </p>
        ) : null}
      </div>

      {lookup.isDebouncing || lookup.isFetching ? (
        <div className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-muted-foreground">
          <Spinner className="tw:size-4" />
          Looking up user…
        </div>
      ) : null}

      {lookup.isError ? (
        <Alert variant="destructive">
          <TriangleAlert className="tw:size-4" />
          <AlertTitle>User lookup failed</AlertTitle>
          <AlertDescription>
            {getErrorDetail(
              lookup.error,
              "Try the lookup again. If the problem continues, contact support.",
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {user ? (
        <div className="tw:rounded-lg tw:border tw:p-4">
          <p className="tw:font-medium">
            {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
              user.email}
          </p>
          <p className="tw:text-sm tw:text-muted-foreground">{user.email}</p>
        </div>
      ) : null}

      {userIsMissing && !isInviting ? (
        <div className="tw:rounded-lg tw:border tw:p-4">
          <p className="tw:text-sm tw:text-muted-foreground">
            No brands account exists for {lookup.normalizedEmail}.
          </p>
          <Button
            type="button"
            variant="outline"
            className="tw:mt-3"
            onClick={() => setIsInviting(true)}
          >
            <Mail className="tw:size-4" />
            Invite {lookup.normalizedEmail}
          </Button>
        </div>
      ) : null}

      {isInviting ? (
        <div className="tw:grid tw:gap-4 sm:tw:grid-cols-2">
          <div className="tw:space-y-2">
            <Label htmlFor="brand-user-first-name">First name</Label>
            <Input
              id="brand-user-first-name"
              value={firstName}
              maxLength={32}
              onChange={(event) => setFirstName(event.target.value)}
            />
          </div>
          <div className="tw:space-y-2">
            <Label htmlFor="brand-user-last-name">Last name</Label>
            <Input
              id="brand-user-last-name"
              value={lastName}
              maxLength={32}
              onChange={(event) => setLastName(event.target.value)}
            />
          </div>
        </div>
      ) : null}

      {assignment.isError ? (
        <Alert variant="destructive">
          <TriangleAlert className="tw:size-4" />
          <AlertTitle>Assignment failed</AlertTitle>
          <AlertDescription>
            {getErrorDetail(
              assignment.error,
              "Your entries were preserved. Try assigning the user again.",
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          disabled={assignment.isPending}
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
        {(user || isInviting) && (
          <Button type="submit" disabled={!canSubmit}>
            {assignment.isPending ? (
              <Spinner className="tw:size-4" />
            ) : (
              <UserPlus className="tw:size-4" />
            )}
            {isInviting ? "Send invitation" : "Assign user"}
          </Button>
        )}
      </DialogFooter>
    </form>
  );
}
